import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, LogOut } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ORG_TYPES = [
  "Destination / Tourism Board",
  "Hotel",
  "Lodge",
  "Hotel/Lodge Collection",
  "DMC",
  "Airline",
  "Transport Company",
  "Travel Agency / Tour Operator",
  "Tourism Activity",
  "Other Tourism Partner",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, memberships, logout, reload, setActiveOrgId } = useAuth();
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!type) {
      setError("Sélectionnez un type d'organisation.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/organizations", { name, type });
      await reload();
      if (data.org?.id) setActiveOrgId(data.org.id);
      navigate(`/organization-setup?org=${data.org.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF6]">
      <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-2.5">
          <Logo className="h-12" />
          <span className="font-heading text-base font-bold text-[#1E3D2B]">Lodge &amp; Co</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-slate-500 sm:inline">{user?.email}</span>
          <button
            onClick={logout}
            data-testid="onboarding-logout"
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1E3D2B]"
          >
            <LogOut size={15} /> Déconnexion
          </button>
        </div>
      </header>

      <div className="mx-auto flex max-w-lg flex-col px-6 py-16">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
            <Building2 size={24} className="text-[#5C9873]" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B]">
            Créez votre organisation
          </h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
            Une organisation dispose de ses propres utilisateurs et données, indépendamment de tout
            office de tourisme.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-5 rounded-2xl border border-slate-200 bg-white p-8"
          data-testid="onboarding-form"
        >
          {error && (
            <div
              data-testid="onboarding-error"
              className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700"
            >
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Nom de l'organisation
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="org-name"
              placeholder="Ex : Alpine Escapes Lodge"
              required
              className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3.5 text-sm text-[#17201A] outline-none focus:border-[#5C9873] focus:ring-2 focus:ring-[#5C9873]/15"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Type d'organisation
            </label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="org-type-trigger" className="h-11">
                <SelectValue placeholder="Sélectionnez un type" />
              </SelectTrigger>
              <SelectContent>
                {ORG_TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`org-type-${t}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="org-submit"
            className="flex h-11 w-full items-center justify-center rounded-lg bg-[#1E3D2B] text-sm font-semibold text-white hover:bg-[#2C5540] disabled:opacity-60"
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            ) : (
              "Créer l'organisation"
            )}
          </button>
        </form>

        {memberships.length > 0 && (
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="onboarding-back"
            className="mt-4 text-center text-sm font-medium text-slate-500 hover:text-[#3E7256]"
          >
            ← Revenir à mon espace
          </button>
        )}
      </div>
    </div>
  );
}
